import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X, Pencil, Check, AlertCircle, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCanMutate } from "@/lib/rbac";

interface MultiValueDetailFieldProps {
  values: string[];
  onChange: (values: string[]) => void;
  icon: React.ReactNode;
  label?: string;
  isLink?: boolean;
  linkPrefix?: string;
  placeholder?: string;
  className?: string;
  validate?: (value: string, existingValues?: string[]) => string | null; // Returns error message or null if valid
  /**
   * Optional handler invoked when the user clicks the displayed primary
   * value (or the "+N" overflow chip). When provided, the value becomes a
   * standalone action (e.g. open compose dialog for an email) and the
   * edit popover opens ONLY via the pencil icon.
   */
  onValueClick?: (value: string) => void;
  hideIcon?: boolean;
}

function EditableValue({ value, idx, isLink, linkPrefix, isPrimary, onSave, onRemove, onMakePrimary, validate, allValues }: {
  value: string; idx: number; isLink?: boolean; linkPrefix?: string; isPrimary: boolean;
  onSave: (idx: number, newVal: string) => void; onRemove: (idx: number) => void;
  onMakePrimary: (idx: number) => void;
  validate?: (value: string, existingValues?: string[]) => string | null;
  allValues?: string[];
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commit = () => {
    const trimmed = draft.trim();

    // If value unchanged, just close edit mode
    if (trimmed === value) {
      setEditing(false);
      setError(null);
      return;
    }

    // Validate if validator provided
    if (trimmed && validate) {
      // Pass other values (excluding current) for duplicate checking
      const otherValues = allValues?.filter((_, i) => i !== idx);
      const validationError = validate(trimmed, otherValues);
      if (validationError) {
        setError(validationError);
        return; // Don't close edit mode if validation fails
      }
    }

    setEditing(false);
    setError(null);
    if (trimmed) onSave(idx, trimmed);
    else setDraft(value);
  };

  if (editing) {
    return (
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 group/item">
          <input
            ref={inputRef}
            value={draft}
            onChange={e => { setDraft(e.target.value); setError(null); }}
            onBlur={() => { setTimeout(commit, 150); }}
            onKeyDown={e => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setDraft(value); setEditing(false); setError(null); }
            }}
            className={cn(
              "flex-1 min-w-0 rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring/20",
              error ? "border-destructive" : "border-input"
            )}
          />
          <button onClick={commit} className="shrink-0 text-accent hover:opacity-80">
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
        {error && (
          <div className="flex items-center gap-1 text-xs text-destructive mt-1">
            <AlertCircle className="h-3 w-3" />
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 group/item">
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        {isLink ? (
          <a href={`${linkPrefix}${value}`} target="_blank" rel="noopener noreferrer" className={cn("text-sm text-accent hover:underline truncate", isPrimary && "font-medium")}>{value}</a>
        ) : (
          <span className={cn("text-sm truncate", isPrimary && "font-medium")}>{value}</span>
        )}
        {isPrimary && (
          <span className="shrink-0 text-[10px] font-medium bg-accent/10 text-accent rounded px-1.5 py-0.5 leading-none">{t("detailSheets.primary")}</span>
        )}
        <button
          onClick={startEdit}
          className="opacity-0 group-hover/item:opacity-100 transition shrink-0 text-muted-foreground hover:text-foreground"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isPrimary && (
          <button
            onClick={() => onMakePrimary(idx)}
            className="opacity-0 group-hover/item:opacity-100 transition text-muted-foreground hover:text-accent"
            title={t("detailSheets.makePrimary")}
          >
            <Star className="h-3 w-3" />
          </button>
        )}
        <button
          onClick={() => onRemove(idx)}
          className="opacity-0 group-hover/item:opacity-100 transition text-muted-foreground hover:text-destructive"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export default function MultiValueDetailField({
  values, onChange, icon, label, isLink, linkPrefix = "https://", placeholder, className, validate, onValueClick, hideIcon,
}: MultiValueDetailFieldProps) {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [popoverOpen, setPopoverOpenRaw] = useState(false);
  // Read-only users never get the popover open, even if upstream code
  // (e.g., +N chip handler we may not have caught) tries to toggle it.
  const setPopoverOpen = (next: boolean) => {
    if (next && !canMutate) return;
    setPopoverOpenRaw(next);
  };
  const inputRef = useRef<HTMLInputElement>(null);

  const addValue = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;

    // Validate if validator provided
    if (validate) {
      const validationError = validate(trimmed, values);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    if (!values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setDraft("");
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const saveValue = (idx: number, newVal: string) => {
    // Check if the new value already exists at a different index
    const existingIdx = values.findIndex(v => v === newVal);
    if (existingIdx !== -1 && existingIdx !== idx) {
      // Value already exists - don't update
      return;
    }
    const updated = [...values];
    updated[idx] = newVal;
    onChange(updated);
  };

  const removeValue = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const makePrimary = (idx: number) => {
    const item = values[idx];
    const rest = values.filter((_, i) => i !== idx);
    onChange([item, ...rest]);
  };

  const primary = values[0] || "";
  const extra = values.length > 1 ? values.length - 1 : 0;

  const hasValueAction = !!onValueClick && !!values[0];

  return (
    <div className={cn("flex items-center gap-3 text-sm", className)}>
      {!hideIcon && <span className="shrink-0">{icon}</span>}
      {label && <span className="text-xs text-muted-foreground shrink-0">{label}</span>}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <div className="flex items-center gap-1.5 group min-w-0">
          {hasValueAction ? (
            <button
              type="button"
              className="text-accent hover:underline truncate text-left min-w-0"
              onClick={e => { e.stopPropagation(); onValueClick!(primary); }}
              title={primary}
            >
              {primary}
            </button>
          ) : isLink ? (
            <a
              href={`${linkPrefix}${primary}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline truncate"
              onClick={e => e.stopPropagation()}
            >
              {primary}
            </a>
          ) : canMutate ? (
            // No value action and no link: clicking the text opens the editor
            <button
              type="button"
              className="text-left truncate hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 -my-0.5 transition min-w-0"
              onClick={e => { e.stopPropagation(); setPopoverOpen(true); }}
            >
              {primary}
            </button>
          ) : (
            <span className="text-left truncate min-w-0" title={primary}>{primary}</span>
          )}
          {extra > 0 && (
            canMutate ? (
              <button
                type="button"
                className="shrink-0 text-[10px] font-medium bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none hover:bg-muted/70 transition"
                onClick={e => { e.stopPropagation(); setPopoverOpen(true); }}
                title={t("detailSheets.showNMore", { count: extra })}
              >
                +{extra}
              </button>
            ) : (
              <span
                className="shrink-0 text-[10px] font-medium bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none"
                title={t("detailSheets.nMore", { count: extra })}
              >
                +{extra}
              </span>
            )
          )}
          {canMutate && (
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t("common.edit")}
                className="shrink-0 rounded p-0.5 hover:bg-muted/40 transition opacity-0 group-hover:opacity-100"
                onClick={e => e.stopPropagation()}
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
          )}
        </div>
        <PopoverContent align="start" className="w-72 p-3 z-[70]" onClick={e => e.stopPropagation()}>
          <div className="space-y-1.5 mb-3">
            {values.length === 0 && (
              <p className="text-xs text-muted-foreground">{t("detailSheets.noValuesYet")}</p>
            )}
            {values.map((val, idx) => (
              <EditableValue
                key={idx}
                value={val}
                idx={idx}
                isLink={isLink}
                linkPrefix={linkPrefix}
                isPrimary={idx === 0}
                onSave={saveValue}
                onRemove={removeValue}
                onMakePrimary={makePrimary}
                validate={validate}
                allValues={values}
              />
            ))}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={draft}
                onChange={e => {
                  setDraft(e.target.value);
                  setError(null); // Clear error on change
                }}
                onKeyDown={e => { if (e.key === "Enter") addValue(); }}
                placeholder={placeholder ?? t("detailSheets.addPlaceholder")}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                className={cn(
                  "flex-1 rounded-md border bg-background px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/20",
                  error ? "border-destructive" : "border-input"
                )}
              />
              <button
                onClick={addValue}
                disabled={!draft.trim()}
                className="shrink-0 rounded-md bg-accent text-accent-foreground p-1.5 hover:opacity-90 disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {error && (
              <div className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertCircle className="h-3 w-3" />
                {error}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
